import React, { useCallback, useMemo, useState } from "react";
import { useGet, usePut } from "../../api";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Search, RefreshCw, Globe, Layers, Edit2, Check } from "react-feather";
import {
  BrandDomainReviewStatus,
  IAiValidatedBrandDomainBatchResult,
  IAiValidatedBrandDomainDto,
  IAiValidatedBrandDomainKey,
  IAiValidatedBrandDomainResponse,
  IAiValidatedBrandDomainValidateResult,
} from "../../interfaces";

type BrandDomainFormState = {
  brandId: string;
  originalDomain: string;
  originalBrandName: string;
  suggestedBrandName: string;
  domain: string;
  countryCode: string;
  validationReason: string;
  reviewStatus: string;
};

// Identify rows by (brandId, domain) — the same key the approve/edit API uses —
// so selection and approval operate at exactly the same granularity as the
// backend. The staging table holds at most one row per (brandId, domain), so
// this key is unique per row; if duplicates ever occur they group into a single
// selectable/approvable unit (matching how the backend keys the approval).
const rowKey = (item: IAiValidatedBrandDomainDto) =>
  `${item.brandId}|${(item.domain ?? "").trim()}`;

const isApproved = (reviewStatus: number | null): boolean =>
  reviewStatus === BrandDomainReviewStatus.Approved;

const reviewStatusLabel = (reviewStatus: number | null): string =>
  isApproved(reviewStatus) ? "Approved" : "Pending";

const parseCountryCodes = (countryCode: string | null): string[] =>
  (countryCode || "")
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);

const toValidationKey = (
  item: IAiValidatedBrandDomainDto
): IAiValidatedBrandDomainKey | null => {
  const domain = (item.domain || "").trim();
  if (!item.brandId || !domain) return null;
  return { brandId: item.brandId, domain };
};

const toFormState = (
  item: IAiValidatedBrandDomainDto
): BrandDomainFormState => ({
  brandId: item.brandId,
  originalDomain: item.domain || "",
  originalBrandName: item.originalBrandName || "",
  suggestedBrandName: item.suggestedBrandName || "",
  domain: item.domain || "",
  countryCode: item.countryCode || "",
  validationReason: item.validationReason || "",
  reviewStatus: reviewStatusLabel(item.reviewStatus),
});

export const BrandDomains: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [brandName, setBrandName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    brandName: "",
    countryCode: "",
  });
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [editingItemKey, setEditingItemKey] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<BrandDomainFormState | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const validateOne = usePut<
    IAiValidatedBrandDomainValidateResult,
    {
      originalDomain?: string;
      domain: string;
      countryCode?: string;
      suggestedBrandName?: string;
      validationReason?: string;
    }
  >();
  const validateMany = usePut<
    IAiValidatedBrandDomainBatchResult,
    { items: IAiValidatedBrandDomainKey[] }
  >();

  const queryParams = useMemo(() => {
    let params = `page=${page}&limit=${limit}`;
    if (appliedFilters.search)
      params += `&search=${encodeURIComponent(appliedFilters.search)}`;
    if (appliedFilters.brandName)
      params += `&brandName=${encodeURIComponent(appliedFilters.brandName)}`;
    if (appliedFilters.countryCode)
      params += `&countryCode=${encodeURIComponent(appliedFilters.countryCode)}`;
    return params;
  }, [page, limit, appliedFilters]);

  const results = useGet<IAiValidatedBrandDomainResponse>(
    `aivalidated-brand-domains?${queryParams}`
  );

  const data = useMemo<IAiValidatedBrandDomainDto[]>(
    () => results.data?.results ?? [],
    [results.data?.results]
  );
  const pagination = results.data?.pagination;

  const hasActiveFilters = Boolean(
    appliedFilters.search ||
      appliedFilters.brandName ||
      appliedFilters.countryCode
  );

  const handleSearch = useCallback(() => {
    setPage(1);
    setSelectedRows(new Set());
    setAppliedFilters({ search, brandName, countryCode });
  }, [search, brandName, countryCode]);

  const handleRefresh = useCallback(async () => {
    setSelectedRows(new Set());
    // refetch rethrows on failure; the hook already stores the error in state
    // for UI rendering, so swallow the rejection to avoid an unhandled promise.
    await results.refetch().catch(() => undefined);
  }, [results]);

  const handleClear = useCallback(() => {
    setSearch("");
    setBrandName("");
    setCountryCode("");
    setPage(1);
    setLimit(25);
    setAppliedFilters({
      search: "",
      brandName: "",
      countryCode: "",
    });
    setSelectedRows(new Set());
  }, []);

  const allSelected =
    data.length > 0 && data.every((item) => selectedRows.has(rowKey(item)));

  const approvableSelectedCount = useMemo(
    () =>
      data.filter(
        (item) =>
          selectedRows.has(rowKey(item)) && (item.domain || "").trim() !== ""
      ).length,
    [data, selectedRows]
  );

  const toggleRowSelection = (key: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedRows(() => {
      if (allSelected) return new Set();
      return new Set(data.map((item) => rowKey(item)));
    });
  };

  const approveOne = useCallback(
    async (item: IAiValidatedBrandDomainDto) => {
      const key = toValidationKey(item);
      if (!key) {
        setActionMessage(null);
        setActionError("Cannot approve a brand domain without a domain value.");
        return;
      }
      setActionError(null);
      setActionMessage(null);
      try {
        await validateOne.execute(
          `aivalidated-brand-domains/validate/${encodeURIComponent(key.brandId)}`,
          { domain: key.domain }
        );
        setActionMessage(`Approved ${key.domain}.`);
        setSelectedRows((prev) => {
          const next = new Set(prev);
          next.delete(rowKey(item));
          return next;
        });
        await results.refetch().catch(() => undefined);
      } catch (error: any) {
        setActionError(
          error?.data?.error ||
            error?.message ||
            "Failed to approve brand domain."
        );
      }
    },
    [validateOne, results]
  );

  const approveSelected = useCallback(async () => {
    const items = data
      .filter((item) => selectedRows.has(rowKey(item)))
      .map(toValidationKey)
      .filter((key): key is IAiValidatedBrandDomainKey => key !== null);

    if (items.length === 0) {
      setActionMessage(null);
      setActionError(
        "Select at least one brand domain with a valid domain to approve."
      );
      return;
    }
    setActionError(null);
    setActionMessage(null);
    try {
      const result = await validateMany.execute(
        "aivalidated-brand-domains/validated",
        { items }
      );
      setActionMessage(
        `Approved ${result.reviewed} of ${result.requested} selected brand domain(s).`
      );
      setSelectedRows(new Set());
      await results.refetch().catch(() => undefined);
    } catch (error: any) {
      setActionError(
        error?.data?.error ||
          error?.message ||
          "Failed to approve selected brand domains."
      );
    }
  }, [data, selectedRows, validateMany, results]);

  const openEdit = (item: IAiValidatedBrandDomainDto) => {
    setEditingItemKey(rowKey(item));
    setEditForm(toFormState(item));
  };

  const closeEdit = () => {
    setEditingItemKey(null);
    setEditForm(null);
  };

  const saveEdit = useCallback(async () => {
    if (!editingItemKey || !editForm) return;
    const brandId = editForm.brandId;
    const domain = editForm.domain.trim();
    const originalDomain = editForm.originalDomain.trim();
    const countryCode = editForm.countryCode.trim().toUpperCase();
    const suggestedBrandName = editForm.suggestedBrandName.trim();
    const validationReason = editForm.validationReason.trim();
    if (!brandId || !domain) {
      setActionMessage(null);
      setActionError("Brand id and domain are required to save and approve.");
      return;
    }
    setActionError(null);
    setActionMessage(null);
    try {
      await validateOne.execute(
        `aivalidated-brand-domains/validate/${encodeURIComponent(brandId)}`,
        {
          originalDomain,
          domain,
          countryCode,
          suggestedBrandName,
          validationReason,
        }
      );
      setActionMessage(`Approved ${domain}.`);
      setEditingItemKey(null);
      setEditForm(null);
      setSelectedRows(new Set());
      await results.refetch().catch(() => undefined);
    } catch (error: any) {
      setActionError(
        error?.data?.error || error?.message || "Failed to approve brand domain."
      );
    }
  }, [editingItemKey, editForm, validateOne, results]);

  const approving = validateOne.loading || validateMany.loading;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 d-flex align-items-center gap-2">
            <Layers size={18} /> Brand Domains
          </h4>
          <div className="text-muted small">
            Browse and filter brand-domain mappings from Kindred data.
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="success"
            size="sm"
            disabled={approvableSelectedCount === 0 || approving}
            onClick={approveSelected}
          >
            <Check size={14} className="me-1" /> Approve Selected ({approvableSelectedCount})
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleRefresh}
            disabled={results.loading}
          >
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
        </div>
      </div>

      {actionMessage && (
        <Alert
          variant="success"
          className="mb-3"
          dismissible
          onClose={() => setActionMessage(null)}
        >
          {actionMessage}
        </Alert>
      )}

      {actionError && (
        <Alert
          variant="danger"
          className="mb-3"
          dismissible
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small">Search Domain</Form.Label>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="example.com"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Label className="small">Brand Name</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Search brand"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small">Country</Form.Label>
              <Form.Control
                size="sm"
                placeholder="US"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
                maxLength={2}
              />
            </Col>
            <Col md={2} className="d-flex gap-2">
              <Button size="sm" variant="primary" onClick={handleSearch}>
                Search
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={handleClear}>
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {results.error && (
        <Alert variant="danger" className="mb-3">
          Failed to load brand domains:{" "}
          {results.error.data?.error || results.error.message}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0" size="sm">
            <thead>
              <tr>
                <th style={{ width: 48 }}>
                  <Form.Check
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all brand domains"
                  />
                </th>
                <th style={{ width: 240 }}>Original Brand Name</th>
                <th style={{ width: 240 }}>Suggested Brand Name</th>
                <th>Domain</th>
                <th style={{ width: 150 }}>Country Codes</th>
                <th style={{ width: 130 }}>Review Status</th>
                <th style={{ width: 260 }}>Validation Reason</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.loading && (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading brand domains...
                  </td>
                </tr>
              )}

              {!results.loading && data.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    {hasActiveFilters
                      ? "No pending brand domains match your filters."
                      : "No brand domains pending review."}
                  </td>
                </tr>
              )}

              {!results.loading &&
                data.map((item) => (
                  <tr key={rowKey(item)}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedRows.has(rowKey(item))}
                        onChange={() => toggleRowSelection(rowKey(item))}
                        aria-label={`Select ${
                          item.domain || item.originalBrandName || "brand domain"
                        }`}
                      />
                    </td>
                    <td>
                      <div className="fw-semibold">{item.originalBrandName || "Unknown"}</div>
                      <div className="text-muted small">{item.brandId}</div>
                    </td>
                    <td>{item.suggestedBrandName || "-"}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Globe size={14} className="text-muted" />
                        <span>{item.domain || "-"}</span>
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const codes = parseCountryCodes(item.countryCode);
                        if (codes.length === 0) {
                          return <span className="text-muted">-</span>;
                        }
                        return (
                          <>
                            {codes.slice(0, 3).map((code) => (
                              <Badge
                                key={code}
                                bg="light"
                                text="dark"
                                className="me-1"
                              >
                                {code}
                              </Badge>
                            ))}
                            {codes.length > 3 && (
                              <Badge bg="secondary">+{codes.length - 3}</Badge>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      <Badge
                        bg={
                          isApproved(item.reviewStatus) ? "success" : "secondary"
                        }
                      >
                        {reviewStatusLabel(item.reviewStatus)}
                      </Badge>
                    </td>
                    <td>{item.validationReason || "-"}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEdit(item)}
                        >
                          <Edit2 size={14} className="me-1" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="success"
                          disabled={
                            approving ||
                            isApproved(item.reviewStatus) ||
                            !item.domain?.trim()
                          }
                          onClick={() => approveOne(item)}
                        >
                          <Check size={14} className="me-1" /> Approve
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Card.Body>
        {pagination && pagination.totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </small>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page <= 1}
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                  setSelectedRows(new Set());
                }}
              >
                Prev
              </Button>
              <Form.Select
                size="sm"
                style={{ width: 120 }}
                value={String(limit)}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                  setSelectedRows(new Set());
                }}
              >
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </Form.Select>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={!pagination || page >= pagination.totalPages}
                onClick={() => {
                  setPage((p) => p + 1);
                  setSelectedRows(new Set());
                }}
              >
                Next
              </Button>
            </div>
          </Card.Footer>
        )}
      </Card>

      <Modal show={!!editingItemKey} onHide={closeEdit} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Brand Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editForm && (
            <Row className="g-3">
              <Col md={12}>
                <div className="text-muted small">
                  Correct any details below, then approve. The original brand
                  name and review status are shown for reference only.
                </div>
              </Col>
              <Col md={6}>
                <Form.Label>Original Brand Name</Form.Label>
                <Form.Control value={editForm.originalBrandName} readOnly />
              </Col>
              <Col md={6}>
                <Form.Label>Suggested Brand Name</Form.Label>
                <Form.Control
                  value={editForm.suggestedBrandName}
                  onChange={(e) =>
                    setEditForm((prev) =>
                      prev
                        ? { ...prev, suggestedBrandName: e.target.value }
                        : prev
                    )
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label>Domain</Form.Label>
                <Form.Control
                  value={editForm.domain}
                  onChange={(e) =>
                    setEditForm((prev) =>
                      prev ? { ...prev, domain: e.target.value } : prev
                    )
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label>Country Code</Form.Label>
                <Form.Control
                  value={editForm.countryCode}
                  onChange={(e) =>
                    setEditForm((prev) =>
                      prev
                        ? { ...prev, countryCode: e.target.value.toUpperCase() }
                        : prev
                    )
                  }
                />
                 <Form.Text className="text-muted">
                  Comma-separated for multiple countries (e.g. US,CA,GB) — a
                  brand_domains row is created for each one on approval.
                </Form.Text>
              </Col>
              <Col md={6}>
                <Form.Label>Review Status</Form.Label>
                <Form.Control value={editForm.reviewStatus} disabled readOnly />
                <Form.Text className="text-muted">
                  Saving approves this brand domain and writes it to Kindred.
                </Form.Text>
              </Col>
              <Col md={12}>
                <Form.Label>Validation Reason</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editForm.validationReason}
                  onChange={(e) =>
                    setEditForm((prev) =>
                      prev
                        ? { ...prev, validationReason: e.target.value }
                        : prev
                    )
                  }
                />
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeEdit}>
            Close
          </Button>
          <Button
            variant="success"
            onClick={saveEdit}
            disabled={approving || !editForm?.domain.trim()}
          >
            <Check size={14} className="me-1" /> Save &amp; Approve
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
